import Base from './base'
import env from '../env'
import AuthToken from './auth_token'
import _ from 'underscore'
import $ from 'jquery'
import URI from 'urijs'

/*
 * Manage user and participant sessions. Take care of initial data loads as well.
 * @namespace Model
 */
export default class Session extends Base {
  // Login/logout URL.
  get url () { return '/api/sessions' }

  /**
   * @param {User} user
   * @param {Participant} participant
   * @param {Object} options
   * @param {Boolean} options.upgradePresenter Upgrade user to a presenter account if they don't have an account
   */
  constructor (user, participant, options = {}) {
    super(...arguments)

    this.options = Object.assign({
      upgradePresenter: false
    }, options)

    /*
     * Hang on to users and participants so that
     * we can do a bunch of crusty stuff with the models
     * in a way the rest of the app doesn't need to worry about.
     */
    this.user = user
    this.participant = participant
  }

  /**
   * Take a username and password to create a session cookie.
   * If a token is provided for authentication, it will use that.
   *
   * @returns {JQuery.Deferred}
   */
  authenticate (login, password) {
    const json = {
      login,
      password
    }
    const url = URI(this.url)

    if (this.options.upgradePresenter) {
      url.query({ upgrade_presenter: true })
    }

    return this.authEvents(this.sync('create', this, { url: url.toString(), attrs: json }))
  }

  /**
   * Clear the logged in user session
   * Also at our root domain if possible
   *
   * @returns {JQuery.Deferred}
   */
  destroy () {
    // Log out of www.polleverywhere.com
    let logoutPromise
    if (env.get('token_auth_enabled')) {
      logoutPromise = this.authToken().logout()
    } else {
      logoutPromise = $.Deferred().resolve()
    }

    const deferred = this.stopped()

    this.sync('delete', this, null).done(() => {
      this.user.clear()
      this.participant.clear()
      this._started = null
      deferred.resolve()
    })

    return $.when(deferred, logoutPromise)
      .done(() => this.trigger('session:stopped'))
  }

  configure (user, participant, account) {
    if (account) {
      // If account data is included, attach it to the user model.
      user.account = account
    }

    this.user.set(user, { parse: true })
    this.participant.set(participant, { parse: true })
    this._stopped = null
    this.trigger('session:started')
  }

  /**
   * Start the session with a pre-fetched auth token
   *
   * @param {String} token Auth token
   * @returns {JQuery.Deferred}
   */
  startWithToken (token) {
    const deferred = this.started()

    this.authToken().token = token

    this.authenticateWithToken()
      .then(deferred.resolve)
      .fail(deferred.reject)

    return deferred.promise()
  }

  /**
   * Start the session with pre-fetched JSON data
   *
   * @param {Object} options
   * @param {Boolean} options.skipAuthToken True to skip trying to auth with existing session first
   * @returns {JQuery.Deferred}
   */
  startWithProfile (json, options = {}) {
    const deferred = this.started()

    /*
     * Use token auth if its enabled for the env OR if we have a token
     * already available that we want to use
     */
    const tokenAvailable = env.get('token_auth_enabled') || (this.authToken().token != null)

    /*
     * skipAuthToken prevents this from becoming an infinite loop in the
     * case where token auth requests succeed, but the profile JSON still
     * doesn't include a user. This isn't expected, but we should be
     * defensive. (It also makes testing with spies easier.)
     */
    if (!options.skipAuthToken && _.isEmpty(json.user) && tokenAvailable) {
      // Proceed as usual when token auth fails
      this.authenticateWithToken()
        .fail(() => this.configure(json.user, json.participant, json.account))
        .always(() => deferred.resolve())
    } else {
      this.configure(json.user, json.participant, json.account)
      deferred.resolve()
    }

    return deferred
  }

  /**
   * Load get data from our API to populate user and participant
   *
   * @param {Object} options
   * @param {Array} options.includes List of param values to send along with the
   *  request to the endpoint within an `include` param.
   * @returns {JQuery.Deferred}
   */
  start (options = {}) {
    const deferred = this.started()

    /*
     * We need to make allow this method to be called more than just once.
     * If you're not logged in intially, this will be called again
     * once you're logged in.
     */
    this.loadProfile(options.includes).done(json => {
      this.startWithProfile(json, options)
        .then(deferred.resolve)
        .fail(deferred.reject)
    }).fail(deferred.reject)

    return deferred
  }

  /**
   * Give access to the deferrable created when starting a session.
   *
   * Note that this is only for initial session starting, if later
   * re-auth occurs (e.g if started as an anon participant, then
   * logging in as a user) this promise will not be updated.
   *
   * @returns {JQuery.Deferred}
   */
  started () {
    return this._started || (this._started = $.Deferred())
  }

  /**
   * Give access to the deferrable created when stopping a session.
   *
   * @returns {JQuery.Deferred}
   */
  stopped () {
    return this._stopped || (this._stopped = $.Deferred())
  }

  // private

  /*
   * Load profile with retry. If status is 0, that means there was
   * a network error so retry it 3 times before failing
   * @param {Array} includes List of `include` param values to send with the
   *  request to the endpoint. The following include values are supported:
   *  - `include=account` to have the API include user account details.
   */
  loadProfile (includes = ['account']) {
    let retry = 0
    const maxRetries = 3
    const timeoutIncrement = 2000

    const fetch = dfd => {
      const options = { url: '/api/profile' }

      if (includes.length) {
        options.data = options.data || {}
        options.data.include = includes.join(',')
      }

      return this.sync('read', this, options)
        .fail(function (xhr) {
          if (!xhr.status && (retry < maxRetries)) {
            retry += 1

            return setTimeout(() => fetch(dfd)
              , timeoutIncrement * retry)
          } else {
            return dfd.reject.apply(this, arguments)
          }
        }).then(function () {
          return dfd.resolve.apply(this, arguments)
        })
    }

    return $.Deferred(fetch)
  }

  authenticateWithToken () {
    return this.authEvents(this.authToken().authenticate())
  }

  authToken () {
    return this._authToken || (this._authToken = new AuthToken())
  }

  authEvents (promise) {
    const deferred = $.Deferred()

    promise
      .fail(json => {
        this.trigger('authentication:fail', json)
        deferred.reject(json)
      }).done(() => {
        this.trigger('authentication:success')
        this.loadProfile().done(json => {
          this.configure(json?.user, json?.participant, json?.account)
          deferred.resolve(json)
        })
      })

    return deferred.promise()
  }
}
